import { useState, useMemo, useEffect, useRef } from "react";
import SpendingDonutChartWithLegend from "../../charts/finances/SpendingDonutWithLegend";
import { useSpendingBreakdown } from "../../../../hooks/finances/useSpendingBreakdown";
import GhostDonutBreakdown from "../../loading/skeletons/GhostDonutBreakdown";
import GhostCardContainer from "../../loading/skeletons/GhostCardContainer";
import { useRefreshOnAuth } from "../../../../hooks/useRefreshOnAuth";
import { getLanguageContent } from "../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../utils/schemas/language/pages/personalFinancesSchema";

const EMPTY_BREAKDOWN = { categories: [], accounts: [] };

function SpendingBreakdownCard() {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
  const [activeInstitution, setActiveInstitution] = useState("All");
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const { isLoading, error, breakdown, refresh } = useSpendingBreakdown();

  useRefreshOnAuth(refresh);

  // --- Track Screen Width (Active only below 470px) ---
  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth <= 960;
      setIsMobile(mobileView);
      if (!mobileView) {
        setCarouselIndex(0); // Reset index on desktop/tablet
      }
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- Touch Swipe Gesture Logic ---
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    if (!isMobile) return;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (!isMobile) return;
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isMobile || !touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 40;
    const isRightSwipe = distance < -40;

    if (isLeftSwipe && carouselIndex < 1) {
      setCarouselIndex(1);
    } else if (isRightSwipe && carouselIndex > 0) {
      setCarouselIndex(0);
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const institutions = useMemo(() => Object.keys(breakdown || {}), [breakdown]);

  useEffect(() => {
    if (!institutions.length) return;
    if (!institutions.includes(activeInstitution)) {
      setActiveInstitution(
        institutions.includes("All") ? "All" : institutions[0],
      );
    }
  }, [activeInstitution, institutions]);

  const currentData = useMemo(() => {
    return breakdown?.[activeInstitution] || breakdown?.All || EMPTY_BREAKDOWN;
  }, [activeInstitution, breakdown]);

  const categoryTotal = useMemo(() => {
    return currentData.categories.reduce((acc, item) => acc + item.value, 0);
  }, [currentData]);

  const accountTotal = useMemo(() => {
    return currentData.accounts.reduce((acc, item) => acc + item.value, 0);
  }, [currentData]);

  if (isLoading) {
    return (
      <GhostCardContainer>
        <GhostDonutBreakdown />
      </GhostCardContainer>
    );
  }

  if (error) {
    return (
      <div className="standard-card-container" style={{ padding: "20px" }}>
        <h4 className="carousel-title margin-reset">
          {content.spendings.breakdown.title}
        </h4>
        <span style={{ fontSize: "14px", color: "#6B7280" }}>{error}</span>
      </div>
    );
  }

  return (
    <div className="standard-card-container mobile-reset-standard-card-container">
      <div className="spending-breakdown-header">
        <div className="scc-header" style={{ width: "100%", margin: 0 }}>
          <h4 className="carousel-title margin-reset">
            {content.spendings.breakdown.title}
          </h4>

          {/* Carousel Arrows - Visible only at <= 470px */}
          <div className="mobile-breakdown-arrows">
            <img
              src="/images/icons/arrow_down.svg"
              alt="Previous"
              className="arrow-carousel arrow-left"
              style={{
                cursor: carouselIndex === 0 ? "default" : "pointer",
                opacity: carouselIndex === 0 ? 0.2 : 1,
              }}
              onClick={() => carouselIndex > 0 && setCarouselIndex(0)}
            />
            <img
              src="/images/icons/arrow_down.svg"
              alt="Next"
              className="arrow-carousel arrow-right"
              style={{
                cursor: carouselIndex === 1 ? "default" : "pointer",
                opacity: carouselIndex === 1 ? 0.2 : 1,
              }}
              onClick={() => carouselIndex < 1 && setCarouselIndex(1)}
            />
          </div>
        </div>

        <div className="full-width align-center" style={{ display: "flex" }}>
          <div className="utc-header-filtering">
            {institutions.map((inst) => (
              <div
                key={inst}
                className={`utc-header-filtering-item ${
                  activeInstitution === inst ? "active" : ""
                }`}
                onClick={() => setActiveInstitution(inst)}
                style={{
                  cursor: "pointer",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                }}
              >
                {inst === "All" ? content.spendings.breakdown.all : inst}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Body Wrapper with Touch Swipe Listener */}
      <div
        className="spending-breakdown-body-wrapper"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="spending-breakdown-body"
          style={{
            transform: isMobile
              ? `translateX(-${carouselIndex * 100}%)`
              : "none",
          }}
        >
          <div className="spending-donut-chart-section">
            <div className="spending-donut-chart-container">
              <SpendingDonutChartWithLegend
                data={currentData.categories}
                centerLabel={content.spendings.breakdown.by_category}
                centerValue={categoryTotal}
                size={isMobile ? 150 : 200}
              />
            </div>
          </div>

          <div className="spending-donut-chart-section">
            <div className="spending-donut-chart-container">
              <SpendingDonutChartWithLegend
                data={currentData.accounts}
                centerLabel={content.spendings.breakdown.by_account}
                centerValue={accountTotal}
                size={isMobile ? 150 : 200}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pagination Dots - Visible only at <= 470px */}
      <div className="mobile-breakdown-dots">
        <div
          className={`dot ${carouselIndex === 0 ? "active" : ""}`}
          onClick={() => setCarouselIndex(0)}
        />
        <div
          className={`dot ${carouselIndex === 1 ? "active" : ""}`}
          onClick={() => setCarouselIndex(1)}
        />
      </div>
    </div>
  );
}

export default SpendingBreakdownCard;
